import type { QuotationSupplierProduct, SupplierPriceSummary } from 'apis/restaurant-inquiries';
import { quotationSuppliersEndpoint } from 'apis/restaurant-inquiries';
import { useCreateDealFromMasterApi } from 'apis/supplier-prices';
import {
  Button,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Text,
  useDisclosure,
} from 'design-system';
import { numOnly } from 'pages/inquiry-processor/utils/num-only';
import { useState } from 'react';
import { useQueryClient } from 'react-query';

interface Props {
  product: QuotationSupplierProduct;
  defaultDealPrice?: string;
  onCreated: (price: SupplierPriceSummary) => void;
}

// Inline price creation for a master product the supplier has no price for,
// right in the PriceLadder's Supplier price slot: one required price, no
// navigation to the Prices screen. `POST cms/exclusive-deals/from-master`
// records it for (supplier, master product), live straight away (the Prices
// screen is where a price is switched off or given an expiry). Covers the
// workbench case: "we negotiated a price, record it as the supplier's price
// while quoting".
export const InlineAddDealPopover = ({ product, defaultDealPrice, onCreated }: Props) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const queryClient = useQueryClient();
  const { mutate: createPrice, isLoading } = useCreateDealFromMasterApi();
  const [dealPrice, setDealPrice] = useState('');

  const openWithDefaults = () => {
    setDealPrice(defaultDealPrice || '');
    onOpen();
  };

  const canSubmit = Number(dealPrice) > 0;

  const submit = () => {
    const price = Number(dealPrice);
    if (!canSubmit) return; // the disabled button already communicates it
    createPrice(
      {
        body: {
          supplierId: product.supplierId,
          masterProductId: product.masterProductId ?? product.id,
          dealPrice: price,
        },
      },
      {
        onSuccess: (res: any) => {
          const dto = res?.data ?? res ?? {};
          // The quotation catalog caches refetch lazily; the caller patches the
          // in-flight selection so the ladder shows the price immediately.
          queryClient.invalidateQueries([quotationSuppliersEndpoint]);
          onCreated({
            id: Number(dto.id) || 0,
            dealPrice: dto.dealPrice ?? price,
            lastModifiedAt: dto.lastModifiedAt ?? new Date().toISOString(),
            isActive: dto.isActive ?? true,
          });
          onClose();
        },
      }
    );
  };

  return (
    <Popover isOpen={isOpen} onClose={onClose} placement='bottom-start' isLazy>
      <PopoverTrigger>
        <Button size='xs' variant='link' colorScheme='green' onClick={openWithDefaults}>
          + Add price
        </Button>
      </PopoverTrigger>
      <PopoverContent w='260px'>
        <PopoverArrow />
        <PopoverBody p='3'>
          <Text fontSize='sm' fontWeight='semibold' mb='1'>
            New supplier price
          </Text>
          <Text fontSize='xs' color='gray.500' noOfLines={2} mb='2'>
            {[product.nameEn || `Master product #${product.id}`, product.brandEn].filter(Boolean).join(' · ')}
          </Text>
          <FormControl isRequired mb='3'>
            <FormLabel fontSize='xs' mb='1'>
              Price (AED)
            </FormLabel>
            <Input
              size='sm'
              value={dealPrice}
              onChange={(e) => setDealPrice(numOnly(e.target.value))}
              placeholder='e.g. 9.50'
              autoFocus
            />
          </FormControl>
          <Text fontSize='xs' color='gray.500' mb='3'>
            Restaurants see this price right away. Switch it off on the Prices screen.
          </Text>
          <Flex gap='2' justify='flex-end'>
            <Button size='xs' variant='outline' onClick={onClose} isDisabled={isLoading}>
              Cancel
            </Button>
            <Button size='xs' colorScheme='green' onClick={submit} isLoading={isLoading} isDisabled={!canSubmit}>
              Save price
            </Button>
          </Flex>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};
